import * as ContextMenu from "@radix-ui/react-context-menu";
import { Ban, BellRing, DoorOpen, Pencil, ChevronRight, HeadphoneOff, MicOff, ScreenShareOff, ShieldCheck, UserMinus, UserX } from "lucide-react";
import { useT } from "../i18n";
import { cn } from "../lib/cn";
import { isTeamSpeak } from "../lib/platform";
import { canActOn, canGiveGroup, ADMIN_GROUP, MEMBER_GROUP, orderedGroups, toggleGroup } from "../lib/permissions";
import { controller } from "../state/controller";
import { shareOf } from "../state/reducer";
import { useSession, useUi, type PersonRef } from "../state/stores";
import { useChannelPermission, useMyWho, usePermission, useWhoOf } from "./hooks";
import { menuContent, menuItem, menuItemDanger, menuSeparator } from "./kit";

/**
 * Kick, ban, remove and role items for the context menu of a person (in the channel
 * tree or the member list). Each shows only if I may use it; role toggles that
 * would break a rule are disabled instead. On TeamSpeak servers the server decides,
 * so the TeamSpeak actions (poke, kick from the channel, ban) are offered to everyone.
 */
export function ModerationItems({ person, groups }: { person: PersonRef; groups: number[] }) {
  const t = useT();
  const isMe = useSession((s) => s.me?.uid === person.uid);
  const kind = useSession((s) => s.kind);
  const defs = useSession((s) => s.groups);
  const me = useMyWho();
  const rank = useWhoOf(person.uid, groups);
  const canKick = usePermission("kick_members");
  const canBan = usePermission("ban_members");
  const canGroups = usePermission("manage_roles");
  const canRemove = usePermission("manage_members");
  const canRename = usePermission("manage_members");
  // Muting for everyone: in the channel the person is in.
  const target = useSession((s) => (person.session !== undefined ? s.clients[person.session] : undefined));
  const canMuteHere = useChannelPermission(target?.channel ?? null, "mute_members");
  // TeamSpeak servers keep their own roles and member list: they get TeamSpeak's own actions instead.
  const onGwar = kind === "gwar";
  const online = person.session !== undefined;
  const onTs = kind === "teamspeak" && !isMe && online;
  // Only someone ranked below me (docs/roles.md).
  const stronger = onGwar && !canActOn(me, rank, defs);
  const showRename = onGwar && (isMe || (canRename && !stronger));
  const showPoke = onTs;
  const showKickChannel = onTs && canKick;
  const showKick = !isMe && canKick && online;
  const showBan = !isMe && canBan && (onGwar || onTs);
  const showRoles = !isMe && onGwar && canGroups;
  const showRemove = !isMe && onGwar && canRemove;
  const showVoice = !isMe && onGwar && !!target && target.channel !== null && canMuteHere;
  // A moderator of the channel, ranked above the sharer, may stop their screen share.
  const share = useSession((s) => (person.session !== undefined ? shareOf(s, person.session) : undefined));
  const showStopShare = !isMe && onGwar && !!share && canMuteHere && !stronger;
  if (!showRename && !showPoke && !showKick && !showBan && !showRoles && !showRemove && !showVoice && !showStopShare) return null;

  const roles = orderedGroups(defs).filter((g) => g.id !== MEMBER_GROUP);

  return (
    <>
      <ContextMenu.Separator className={menuSeparator} />
      {showRename && (
        <ContextMenu.Item className={menuItem} onSelect={() => useUi.getState().openDialog({ kind: "nickname", uid: person.uid })}>
          <Pencil className="size-4" /> {t("nickname.change")}
        </ContextMenu.Item>
      )}
      {showRoles && (
        <ContextMenu.Sub>
          <ContextMenu.SubTrigger className={cn(menuItem, "justify-between")}>
            <span className="flex items-center gap-2">
              <ShieldCheck className="size-4" /> {t("mod.roles")}
            </span>
            <ChevronRight className="size-4 text-subtle" />
          </ContextMenu.SubTrigger>
          <ContextMenu.Portal>
            <ContextMenu.SubContent className={cn(menuContent, "w-56")} sideOffset={4}>
              {roles.length === 0 && <div className="px-2 py-1.5 text-xs text-subtle">{t("mod.noRoles")}</div>}
              {roles.map((g) => {
                const on = groups.includes(g.id);
                // Only roles below my rank, and not for someone ranked at or above me.
                const blocked = stronger || !canGiveGroup(me, g, defs);
                return (
                  <ContextMenu.CheckboxItem
                    key={g.id}
                    checked={on}
                    disabled={blocked}
                    title={blocked ? t("mod.roleBlocked") : undefined}
                    onCheckedChange={(next) => {
                      void controller.attempt(controller.setMemberGroups(person.uid, toggleGroup(groups, g.id, next)));
                    }}
                    className={cn(menuItem, "pl-7")}
                  >
                    <ContextMenu.ItemIndicator className="absolute left-2 text-accent">
                      <span className="block size-2 rounded-full bg-accent" />
                    </ContextMenu.ItemIndicator>
                    <span className="size-2.5 shrink-0 rounded-full border border-line-strong" style={{ background: g.color ?? "transparent" }} />
                    <span className="min-w-0 flex-1 truncate">{g.name}</span>
                    {g.id === ADMIN_GROUP && <ShieldCheck className="size-3.5 text-subtle" />}
                  </ContextMenu.CheckboxItem>
                );
              })}
            </ContextMenu.SubContent>
          </ContextMenu.Portal>
        </ContextMenu.Sub>
      )}
      {showVoice && (
        <>
          <ContextMenu.Item
            className={menuItem}
            disabled={stronger}
            onSelect={() => void controller.attempt(controller.setVoiceFor(person.session!, { muted: !target!.server_muted }))}
          >
            <MicOff className="size-4" /> {t(target!.server_muted ? "mod.unmuteAll" : "mod.muteAll")}
          </ContextMenu.Item>
          {/* A TeamSpeak user hears through the puppets, which a deafen does not reach (docs/roles.md). */}
          {!isTeamSpeak(target!.platform) && (
            <ContextMenu.Item
              className={menuItem}
              disabled={stronger}
              onSelect={() => void controller.attempt(controller.setVoiceFor(person.session!, { deafened: !target!.server_deafened }))}
            >
              <HeadphoneOff className="size-4" /> {t(target!.server_deafened ? "mod.undeafenAll" : "mod.deafenAll")}
            </ContextMenu.Item>
          )}
        </>
      )}
      {showStopShare && (
        <ContextMenu.Item className={menuItem} onSelect={() => void controller.screen.stopOther(share!.share)}>
          <ScreenShareOff className="size-4" /> {t("screen.stopOther")}
        </ContextMenu.Item>
      )}
      {showPoke && (
        <ContextMenu.Item className={menuItem} onSelect={() => useUi.getState().openDialog({ kind: "poke", person })}>
          <BellRing className="size-4" /> {t("poke.action")}
        </ContextMenu.Item>
      )}
      {showKickChannel && (
        <ContextMenu.Item className={cn(menuItem, menuItemDanger)} onSelect={() => void controller.kickFromChannel(person.session!, person.uid)}>
          <DoorOpen className="size-4" /> {t("tree.kickChannel")}
        </ContextMenu.Item>
      )}
      {showKick && (
        <ContextMenu.Item
          className={cn(menuItem, menuItemDanger)}
          disabled={stronger}
          onSelect={() =>
            useUi.getState().openDialog({
              kind: "confirm",
              title: t("tree.kickTitle", { name: person.nickname }),
              memberTitle: { uid: person.uid, fallback: person.nickname, key: "tree.kickTitle" },
              body: t("tree.kickBody"),
              confirmLabel: t("tree.kick"),
              danger: true,
              onConfirm: () => void controller.kickClient(person.session!, person.uid),
            })
          }
        >
          <UserX className="size-4" /> {t("tree.kick")}
        </ContextMenu.Item>
      )}
      {showBan && (
        <ContextMenu.Item
          className={cn(menuItem, menuItemDanger)}
          disabled={stronger}
          onSelect={() => useUi.getState().openDialog({ kind: "ban", person })}
        >
          <Ban className="size-4" /> {t("mod.ban")}
        </ContextMenu.Item>
      )}
      {showRemove && (
        <ContextMenu.Item
          className={cn(menuItem, menuItemDanger)}
          disabled={stronger}
          onSelect={() => useUi.getState().openDialog({ kind: "removeMember", person })}
        >
          <UserMinus className="size-4" /> {t("mod.remove")}
        </ContextMenu.Item>
      )}
    </>
  );
}
